import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../../Config/axiosInstance";
import {
  Button,
  Typography,
  Box,
  Chip,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import BusinessIcon from "@mui/icons-material/Business";
import DeleteIcon from "@mui/icons-material/Delete";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaidIcon from "@mui/icons-material/Paid";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";

import "./DetailsInvoice.css";
import AddPartialPayment from "./AddPartialPayment";
import EditPartialPayment from "./EditPartialPayment";
import PaymentDeleteDialog from "./PaymentDeleteDialog";

const DetailsInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState({});
  const [partialPayments, setPartialPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaymentPopupOpen, setIsPaymentPopupOpen] = useState(false);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canView, setCanView] = useState(false);
  const [canEditInvoice, setCanEditInvoice] = useState(false);

  //Formatează numerele în stil românesc:punct la mii și virgulă la zecimale.
  const formatNumber = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const n = Number(value);
    if (Number.isNaN(n)) return "";

    const parts = n.toFixed(2).split(".");
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    const decimalPart = parts[1];

    return `${integerPart},${decimalPart}`;
  };

  //Formatează data într-un format mai prietenos: dd.mm.yyyy.
  const formatDateRO = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("ro-RO", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      console.error("Eroare la formatarea datei:", error);
      return dateString;
    }
  };

  //Dacă un câmp nu are valoare, afișăm "-", ca interfața să rămână curată și consistentă.
  const displayValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    return value;
  };

  /*
    Extrage codurile de proiect unice din liniile facturii.
    Folosim useMemo ca să nu recalculăm inutil la fiecare render.
   */
  const getUniqueProjectCodes = useMemo(() => {
    if (!invoice.invoice_lines_data || invoice.invoice_lines_data.length === 0) {
      return [];
    }

    const uniqueCodes = new Map();
    invoice.invoice_lines_data.forEach((line) => {
      if (line.project_code_label && line.project_code_label !== "-") {
        uniqueCodes.set(line.project_code_label, {
          code: line.project_code_label,
          projectId: line.project_id,
        });
      }
    });

    return Array.from(uniqueCodes.values());
  }, [invoice.invoice_lines_data]);

  // Pregătește codurile de proiect pentru afișare într-un singur text.
  const projectCodesDisplay = useMemo(() => {
    if (getUniqueProjectCodes.length === 0) return "-";
    return getUniqueProjectCodes.map((pc) => pc.code).join(", ");
  }, [getUniqueProjectCodes]);

  //Citește permisiunile din localStorage pentru pagina de detalii plată.
  useEffect(() => {
    const requiredPath = "/invoices_register/details-payment-invoice-commercial/";
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    setCanCreate(
      permissions.some(
        (page) =>
          new URL(page.url).pathname.startsWith(requiredPath) && page.can_create
      )
    );
    setCanEdit(
      permissions.some(
        (page) =>
          new URL(page.url).pathname.startsWith(requiredPath) && page.can_edit
      )
    );
    setCanDelete(
      permissions.some(
        (page) =>
          new URL(page.url).pathname.startsWith(requiredPath) && page.can_delete
      )
    );
    setCanView(
      permissions.some(
        (page) =>
          new URL(page.url).pathname.startsWith(requiredPath) && page.can_view
      )
    );
  }, []);

  // Verifică separat dacă utilizatorul are voie să editeze factura.
  useEffect(() => {
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    const normalize = (path) => path.replace(/\/+$/, "") + "/";

    const matched = permissions.some((page) => {
      const path = normalize(new URL(page.url).pathname);
      return path === "/invoices_register/" && page.can_edit;
    });

    setCanEditInvoice(matched);
  }, []);

  //Încarcă datele facturii din backend și le pregătește pentru afișare.
  const fetchInvoiceData = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/invoices-register/${id}/`);
      const rawData = response.data;

      const data = Array.isArray(rawData) ? rawData[0] || {} : rawData || {};

      const formattedInvoice = {
        ...data,
        client_display: data.client_label || data.client || "-",
        supplier_display: data.supplier_label || data.supplier || "-",
        contract_display: data.contract_label || data.contract || "-",
        addendum_label: data.addendum_label || "-",  // <-- ADĂUGAT
        project_responsible_display:
          data.project_responsible_label || data.project_responsible || "-",
        currency_display: data.currency_label || data.currency || "-",
        exchange_rate_display:
          data.exchange_rate_value ||
          (data.exchange_rate !== null && data.exchange_rate !== undefined
            ? data.exchange_rate
            : "-"),
        contract_paper_date_display: formatDateRO(data.contract_paper_date),
        invoice_date_display: formatDateRO(data.invoice_date),
        invoice_due_date_display: formatDateRO(data.invoice_due_date),
        receipt_due_date_display: formatDateRO(data.receipt_due_date),
        created_at_display: formatDateRO(data.created_at),
        updated_at_display: formatDateRO(data.updated_at),
        invoice_lines_data: data.invoice_lines_data || [],
        remaining_payment_system_display: data.remaining_payment_system,
        total_paid_display: data.total_paid_display,
        total_paid_system_display: data.total_paid_system_display,
      };

      setInvoice(formattedInvoice);
    } catch (error) {
      console.error("Error fetching invoice details:", error);
    }
  }, [id]);

  // Încarcă plățile parțiale pentru factura curentă.
  const fetchPartialPayments = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/payments-commercial/?invoice=${id}`);

      const filteredPayments = (response.data || [])
        .filter((payment) => payment.invoice?.toString() === id.toString())
        .map((payment) => ({
          ...payment,
          date_display: formatDateRO(payment.date),
        }));

      setPartialPayments(filteredPayments);
    } catch (error) {
      console.error("Error fetching partial payments:", error);
    }
  }, [id]);

  // Încarcă în paralel atât factura, cât și plățile.
  const loadPageData = useCallback(async () => {
    setIsLoading(true);
    try {
      await Promise.all([fetchInvoiceData(), fetchPartialPayments()]);
    } finally {
      setIsLoading(false);
    }
  }, [fetchInvoiceData, fetchPartialPayments]);

  // La deschiderea paginii, încărcăm toate datele necesare.
  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  /*
    După editarea unei plăți:
    - actualizăm local lista
    - închidem popup-ul
    - reîncărcăm datele din backend, ca să fim siguri că totul e sincronizat
   */
  const handleEditPayment = async (updatedPayment) => {
    try {
      setPartialPayments((prev) =>
        prev.map((payment) =>
          payment.id === updatedPayment.id
            ? {
              ...updatedPayment,
              date_display: formatDateRO(updatedPayment.date),
            }
            : payment
        )
      );

      setIsEditPopupOpen(false);
      setSelectedPayment(null);

      await fetchPartialPayments();
      await fetchInvoiceData();
    } catch (error) {
      console.error("Error refreshing invoice after editing payment:", error);
    }
  };

  // Șterge o plată și apoi actualizează lista de plăți și totalurile facturii.
  const handlePaymentDeleted = async (paymentId) => {
    try {
      await axiosInstance.delete(`/payments-commercial/${paymentId}/`);

      setPartialPayments((prev) =>
        prev.filter((payment) => payment.id !== paymentId)
      );

      await fetchInvoiceData();

      setConfirmDeleteOpen(false);
      setPaymentToDelete(null);
    } catch (error) {
      console.error("Error deleting payment: ", error);
      throw error;
    }
  };

  // Deschide dialogul de confirmare pentru ștergere.
  const confirmDelete = (payment) => {
    setPaymentToDelete(payment);
    setConfirmDeleteOpen(true);
  };

  // Alege clasa vizuală pentru badge-ul de payment status.
  const paymentChipClass = useMemo(() => {
    const payment = invoice.payment?.trim();

    if (payment === "Fully settled") return "chip-full-paid";
    if (payment === "Partially settled") return "chip-partial-pay";
    if (payment === "Unsettled") return "chip-unpaid";

    return "chip-unpaid";
  }, [invoice.payment]);

  // Lista de câmpuri afișate în zona principală de informații
  const detailItems = useMemo(
    () => [
      { label: "Project Code", value: projectCodesDisplay },
      { label: "Contract", value: invoice.contract_display },
      { label: "Addendum", value: invoice.addendum_label || "-" },  // <-- NOU
      { label: "Project Responsible", value: invoice.project_responsible_display },
      {
        label: "Contract / Addendum Date",
        value: invoice.contract_paper_date_display,
      },
      { label: "Invoice Date", value: invoice.invoice_date_display },
      { label: "Invoice Due Date", value: invoice.invoice_due_date_display },
      { label: "Invoice Payment Date", value: invoice.receipt_due_date_display },
      { label: "Currency", value: invoice.currency_display },
      { label: "Exchange Rate", value: invoice.exchange_rate_display || "-" },

      {
        label: "Total without VAT",
        value:
          invoice.total_without_vat_display ||
          `${formatNumber(invoice.total_without_vat)} ${invoice.currency_display || ""
            }`.trim(),
      },
      {
        label: "Total with VAT",
        value:
          invoice.total_with_vat_display ||
          `${formatNumber(invoice.total_with_vat)} ${invoice.currency_display || ""
            }`.trim(),
      },
      {
        label: "Total without VAT (System)",
        value:
          invoice.total_without_vat_system_display ||
          formatNumber(invoice.total_without_vat_system),
      },
      {
        label: "Total with VAT (System)",
        value:
          invoice.total_with_vat_system_display ||
          formatNumber(invoice.total_with_vat_system),
      },
      {
        label: "Total Paid",
        value:
          invoice.total_paid_display ||
          `${formatNumber(invoice.total_paid)} ${invoice.currency_display || ""}`.trim(),
      },
      {
        label: "Total Paid (System)",
        value:
          invoice.total_paid_system_display ||
          formatNumber(invoice.total_paid_system),
      },

      { label: "Remarks", value: invoice.description },
    ],
    [invoice, projectCodesDisplay]
  );

  // Cât timp încărcăm datele, afișăm loader-ul paginii.
  if (isLoading) {
    return (
      <div className="invoices-page">
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          <div className="invoice-loading-card">
            <div className="loading-spinner"></div>
            <p>Loading invoice details...</p>
          </div>
        </Box>
      </div>
    );
  }

  return (
    <div className="invoice-details">
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <div className="main-header-row">
          <div className="main-header-left">
            {/* Buton back */}
            <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
            <h2 className="add-invoices-title">Invoice Details</h2>
          </div>
          <div className="main-header-right">
            {/* Numărul facturii afișat sus */}
            <div className="invoice-number-header">
              <AssignmentRoundedIcon sx={{ color: "#3b82f6", fontSize: "20px" }} />
              <span className="invoice-number-header-text">
                {displayValue(invoice.invoice_number)}
              </span>
            </div>
          </div>
        </div>

        <hr className="title-separator" />

        <div className="info-header-row">
          <div className="info-header-left">
            {/* Client și supplier afișate compact */}
            <div className="client-supplier-compact">
              <div className="compact-item">
                <BusinessIcon sx={{ color: "#3b82f6", fontSize: "16px" }} />
                <span className="compact-label">Client:</span>
                <span className="compact-value">{displayValue(invoice.client_display)}</span>
              </div>
              {invoice.supplier_display && invoice.supplier_display !== "-" && (
                <div className="compact-item">
                  <BusinessIcon sx={{ color: "#3b82f6", fontSize: "16px" }} />
                  <span className="compact-label">Supplier:</span>
                  <span className="compact-value">
                    {displayValue(invoice.supplier_display)}
                  </span>
                </div>
              )}
            </div>

            {/* Badge-uri pentru payment status și status general */}
            <div className="invoice-header-badges">
              <Chip label={displayValue(invoice.payment)} className={paymentChipClass} />
              {invoice.status ? (
                <Chip label={invoice.status} className="chip-status-neutral" />
              ) : null}
            </div>
          </div>

          <div className="info-header-right">
            {/* Buton de editare factură, doar dacă utilizatorul are drept */}
            {canEditInvoice && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                className="edit-invoice-btn"
                onClick={() => navigate(`/invoices_register/edit/${id}`)}
              >
                Edit Invoice
              </Button>
            )}
          </div>
        </div>

        <div className="responsive-container details-layout">
          <Box sx={{ flex: 1 }} className="box">
            <div className="section-card">
              <div className="card-header">
                <div className="details-section-title">
                  <ReceiptLongIcon fontSize="small" />
                  <span>Invoice Information</span>
                </div>
              </div>
              <div className="card-content">
                {/* Informațiile principale ale facturii */}
                <div className="details-grid">
                  {detailItems.map((item, index) => (
                    <React.Fragment key={index}>
                      <Typography
                        variant="body1"
                        component="div"
                        className="details-label"
                      >
                        {item.label}
                      </Typography>
                      <Typography
                        variant="body1"
                        component="div"
                        className="details-value"
                      >
                        {displayValue(item.value)}
                      </Typography>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </Box>

          <Box sx={{ flex: 1 }} className="box">
            <div className="section-card">
              <div className="card-header">
                <div className="details-section-title">
                  <PaidIcon fontSize="small" />
                  <span>Payments</span>
                </div>

                {/* Buton pentru adăugare plată nouă */}
                {canCreate && Number(invoice.remaining_payment) > 0 && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    className="add-payment-header-btn"
                    onClick={() => setIsPaymentPopupOpen(true)}
                  >
                    Add New Payment
                  </Button>
                )}
              </div>

              <div className="card-content">
                {canView && (
                  <div>
                    {/* Dacă nu există plăți, afișăm mesaj */}
                    {partialPayments.length === 0 ? (
                      <div className="empty-state-box">No payments registered yet.</div>
                    ) : (
                      partialPayments.map((payment) => (
                        <div key={payment.id} className="payment-row-card">
                          <div className="payment-row-main">
                            <div className="payment-row-date">{payment.date_display}</div>
                            <div className="payment-row-amount">
                              <span className="payment-amount-original">
                                {formatNumber(payment.amount)} {invoice.currency_display || ""}
                              </span>

                              {/* Dacă există și sumă în sistem, o afișăm și pe ea */}
                              {(payment.amount_system_display ||
                                (payment.amount_system !== null &&
                                  payment.amount_system !== undefined &&
                                  payment.amount_system !== "")) && (
                                  <>
                                    <span className="payment-amount-separator">→</span>
                                    <span className="payment-amount-system">
                                      {payment.amount_system_display ||
                                        formatNumber(payment.amount_system)}
                                    </span>
                                  </>
                                )}
                            </div>
                          </div>

                          {/* Acțiuni pe plată: edit / delete */}
                          <div className="payment-row-actions">
                            {canEdit && (
                              <Button
                                variant="text"
                                startIcon={<EditIcon />}
                                sx={{ minWidth: "unset", padding: "4px" }}
                                onClick={() => {
                                  setSelectedPayment(payment);
                                  setIsEditPopupOpen(true);
                                }}
                              />
                            )}
                            {canDelete && (
                              <Button
                                variant="text"
                                color="error"
                                startIcon={<DeleteIcon />}
                                sx={{ minWidth: "unset", padding: "4px" }}
                                onClick={() => confirmDelete(payment)}
                              />
                            )}
                          </div>
                        </div>
                      ))
                    )}

                    {/* Card cu remaining payment */}
                    <div className="remaining-payment-card">
                      <div className="remaining-payment-header">
                        <strong>Remaining Payment</strong>
                      </div>
                      <div className="remaining-payment-amount">
                        <span className="remaining-payment-original">
                          {formatNumber(invoice.remaining_payment)}{" "}
                          {invoice.currency_display || ""}
                        </span>
                        {invoice.remaining_payment_system_display !== undefined &&
                          invoice.remaining_payment_system_display !== null && (
                            <>
                              <span className="remaining-payment-separator">→</span>
                              <span className="remaining-payment-system">
                                {formatNumber(invoice.remaining_payment_system_display)} Euro
                              </span>
                            </>
                          )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Box>
        </div>

        <div className="invoice-lines-section">
          <div className="section-card" style={{ marginBottom: 0 }}>
            <div className="card-header">
              <div className="details-section-title">
                <ViewListRoundedIcon fontSize="small" />
                <span>Invoice Lines</span>
              </div>
            </div>
            <div className="card-content">
              {/* Liniile facturii */}
              {!invoice.invoice_lines_data || invoice.invoice_lines_data.length === 0 ? (
                <div className="empty-state-box">No invoice lines found.</div>
              ) : (
                <div className="invoice-lines-list">
                  {invoice.invoice_lines_data.map((line) => (
                    <div key={line.id} className="invoice-line-card">
                      <div className="invoice-line-top">
                        <div className="invoice-line-title">
                          {displayValue(line.opportunity_label)}
                        </div>
                      </div>

                      <div className="invoice-line-grid">
                        <div className="line-item">
                          <span className="line-item-label">Subproject Code</span>
                          <span className="line-item-value">
                            {displayValue(line.subproject_code_label)}
                          </span>
                        </div>

                        <div className="line-item">
                          <span className="line-item-label">Sum without VAT</span>
                          <span className="line-item-value">
                            {displayValue(
                              line.sum_without_vat_display ||
                              `${formatNumber(line.sum_without_vat)} ${invoice.currency_display || ""
                                }`.trim()
                            )}
                          </span>
                        </div>

                        <div className="line-item">
                          <span className="line-item-label">VAT</span>
                          <span className="line-item-value">
                            {line.vat ? `${formatNumber(line.vat)} %` : "-"}
                          </span>
                        </div>

                        <div className="line-item">
                          <span className="line-item-label">Sum with VAT</span>
                          <span className="line-item-value">
                            {displayValue(
                              line.sum_with_vat_display ||
                              `${formatNumber(line.sum_with_vat)} ${invoice.currency_display || ""
                                }`.trim()
                            )}
                          </span>
                        </div>

                        <div className="line-item">
                          <span className="line-item-label">Sum without VAT (System)</span>
                          <span className="line-item-value">
                            {displayValue(
                              line.sum_system_without_vat_display ||
                              formatNumber(line.sum_system_without_vat)
                            )}
                          </span>
                        </div>

                        <div className="line-item">
                          <span className="line-item-label">Sum with VAT (System)</span>
                          <span className="line-item-value">
                            {displayValue(
                              line.sum_with_vat_system_display ||
                              formatNumber(line.sum_with_vat_system)
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Popup pentru adăugare plată */}
        <AddPartialPayment
          isOpen={isPaymentPopupOpen}
          onClose={() => setIsPaymentPopupOpen(false)}
          onAddPayment={async (newPayment) => {
            const formattedPayment = {
              ...newPayment,
              date_display: formatDateRO(newPayment.date),
            };

            setPartialPayments((prev) => [...prev, formattedPayment]);
            setInvoice((prevInvoice) => ({
              ...prevInvoice,
              remaining_payment:
                parseFloat(prevInvoice.remaining_payment || 0) -
                parseFloat(newPayment.amount || 0),
            }));
            setIsPaymentPopupOpen(false);

            await fetchPartialPayments();
            await fetchInvoiceData();
          }}
          invoiceId={id}
          invoiceNumber={invoice.invoice_number}
          remainingPayment={parseFloat(invoice.remaining_payment || 0)}
          invoiceCurrency={invoice.currency_label}
        />

        {/* Popup pentru editare plată */}
        <EditPartialPayment
          isOpen={isEditPopupOpen}
          onClose={() => setIsEditPopupOpen(false)}
          payment={selectedPayment}
          onUpdatePayment={handleEditPayment}
          invoiceId={id}
          invoiceNumber={invoice.invoice_number}
          remainingPayment={parseFloat(invoice.remaining_payment || 0)}
          invoiceCurrency={invoice.currency_label}
        />

        {/* Dialog pentru confirmare ștergere plată */}
        <PaymentDeleteDialog
          open={confirmDeleteOpen}
          onClose={() => {
            setConfirmDeleteOpen(false);
            setPaymentToDelete(null);
          }}
          payment={paymentToDelete}
          onDeleted={handlePaymentDeleted}
        />
      </Box>
    </div>
  );
};

export default DetailsInvoice;